import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { runA11yAudit, type A11yIssue } from '@/utils/accessibility-audit';

export function A11yReport() {
  const [issues, setIssues] = useState<A11yIssue[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const runAudit = () => {
    const results = runA11yAudit();
    setIssues(results);
    setIsOpen(true);
  };

  const groupedIssues = useMemo(() => {
    return issues.reduce(
      (acc, issue) => {
        if (!acc[issue.severity]) {
          acc[issue.severity] = [];
        }
        acc[issue.severity].push(issue);
        return acc;
      },
      {} as Record<string, A11yIssue[]>
    );
  }, [issues]);

  const severityOrder = ['critical', 'serious', 'moderate', 'minor'] as const;

  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={runAudit}
        className="fixed bottom-4 right-4 z-50"
        aria-label="Run accessibility audit"
      >
        Run A11y Audit
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Accessibility Audit Results</DialogTitle>
            <DialogDescription>
              Found {issues.length} potential accessibility issues
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {severityOrder.map(
              (severity) =>
                groupedIssues[severity]?.length > 0 && (
                  <div key={severity}>
                    <h3 className="mb-3 flex items-center gap-2 font-semibold">
                      <Badge
                        variant={
                          severity === 'critical'
                            ? 'destructive'
                            : severity === 'serious'
                              ? 'error'
                              : severity === 'moderate'
                                ? 'warning'
                                : 'secondary'
                        }
                      >
                        {severity.toUpperCase()}
                      </Badge>
                      <span>{groupedIssues[severity].length} issues</span>
                    </h3>

                    <div className="space-y-4">
                      {groupedIssues[severity].map((issue, index) => (
                        <div
                          key={`${severity}-${index}`}
                          className="space-y-2 rounded-lg border p-4"
                        >
                          <p className="font-medium">{issue.issue}</p>
                          <p className="text-sm text-muted-foreground">
                            <strong>Fix:</strong> {issue.fix}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            <strong>WCAG:</strong> {issue.wcagCriteria}
                          </p>
                          <details className="text-xs">
                            <summary className="cursor-pointer text-muted-foreground">
                              Show element
                            </summary>
                            <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-xs">
                              {issue.element}
                            </pre>
                          </details>
                        </div>
                      ))}
                    </div>
                  </div>
                )
            )}

            {issues.length === 0 && (
              <div className="py-8 text-center">
                <Check className="mx-auto mb-3 size-12 text-success" />
                <p className="text-lg font-semibold">No issues found!</p>
                <p className="text-sm text-muted-foreground">
                  This page passes basic accessibility checks
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
